import { request } from './core';

export type BlockType = 'hero' | 'text' | 'image' | 'gallery_row' | 'quote' | 'callout' | 'faq' | 'services' | 'divider' | 'video' | 'full_bleed' | 'split' | 'statement' | 'process' | 'gallery_list' | 'contact_form' | 'section';

export interface BlockContent {
  // hero
  heading?: string;
  subtext?: string;
  image_asset_id?: string;
  cta_label?: string;
  cta_url?: string;
  // text
  markdown?: string;
  // image
  asset_id?: string;
  caption?: string;
  layout?: 'full' | 'wide' | 'center';
  // quote
  body?: string;
  author?: string;
  // callout
  icon?: string;
  // gallery_row
  columns?: number;
  items?: Array<{ ID: string; AssetID: string; SortOrder: number }>;
  // faq
  faq_items?: Array<{ ID: string; Question: string; Answer: string; SortOrder: number }>;
  // services
  services_items?: Array<{ ID: string; Name: string; Price: string; Description: string; SortOrder: number }>;
  // divider
  style?: 'line' | 'space';
  // video
  url?: string;
  // full_bleed
  height?: 'full' | 'tall' | 'medium';
  overlay?: number;
  text_align?: 'left' | 'center' | 'right';
  // split (body is shared with quote above)
  image_side?: 'left' | 'right';
  // statement
  label?: string;
  align?: 'left' | 'center' | 'start' | 'end' | 'stretch';
  // process
  process_steps?: Array<{ id: string; heading: string; body: string; sort_order: number }>;
  intro?: string;
  // section
  direction?: 'row' | 'column';
  gap?: 'none' | 'sm' | 'md' | 'lg';
  justify?: 'start' | 'center' | 'end' | 'between';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  bg_color?: string;
  // cross-cutting: optional font color override for any text-bearing block
  font_color?: string;
}

export interface Block {
  ID: string;
  PageID: string;
  BlockType: BlockType;
  SortOrder: number;
  ParentBlockID?: string;
  Content: BlockContent;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface Page {
  ID: string;
  UserID: string;
  PortfolioID: string;
  TemplateID: string;
  Slug: string;
  Title: string;
  IsPublished: boolean;
  HasDraft: boolean;
  DraftUpdatedAt: string | null;
  SortOrder: number;
  Blocks: Block[];
  MetaTitle?: string;
  MetaDescription?: string;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface PageTemplate {
  ID: string;
  Name: string;
  Description: string;
  SortOrder: number;
  Blocks: Array<{ BlockType: BlockType; DefaultContent: BlockContent }>;
}

export const pagesApi = {
  listPages: () => request<Page[]>("GET", "/api/v1/pages"),
  seedDefaultPages: () => request<{ seeded: boolean }>("POST", "/api/v1/pages/seed", {}),
  clonePage: (id: string) => request<Page>("POST", `/api/v1/pages/${encodeURIComponent(id)}/clone`, {}),
  listPageTemplates: () => request<PageTemplate[]>("GET", "/api/v1/pages/templates"),
  getPage: (id: string) => request<Page>("GET", `/api/v1/pages/${encodeURIComponent(id)}`),
  createPage: (p: { slug: string; title: string; template_id?: string }) =>
    request<Page>("POST", "/api/v1/pages", p),
  updatePage: (id: string, p: { title?: string; slug?: string; sort_order?: number; meta_title?: string; meta_description?: string }) =>
    request<Page>("PUT", `/api/v1/pages/${encodeURIComponent(id)}`, p),
  publishPage: (id: string) =>
    request<Page>("POST", `/api/v1/pages/${encodeURIComponent(id)}/publish`, {}),
  unpublishPage: (id: string) =>
    request<Page>("POST", `/api/v1/pages/${encodeURIComponent(id)}/unpublish`, {}),
  deletePage: (id: string) => request<void>("DELETE", `/api/v1/pages/${encodeURIComponent(id)}`),
  addBlock: (pageId: string, b: { block_type: BlockType; content: BlockContent; parent_block_id?: string }) =>
    request<Block>("POST", `/api/v1/pages/${encodeURIComponent(pageId)}/blocks`, b),
  updateBlock: (pageId: string, blockId: string, content: BlockContent) =>
    request<Block>("PUT", `/api/v1/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`, { content }),
  deleteBlock: (pageId: string, blockId: string) =>
    request<void>("DELETE", `/api/v1/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`),
  reorderBlocks: (pageId: string, blockIds: string[]) =>
    request<void>("PUT", `/api/v1/pages/${encodeURIComponent(pageId)}/blocks/order`, { block_ids: blockIds }),
  moveBlock: (pageId: string, blockId: string, parentBlockId: string | null) =>
    request<void>("PATCH", `/api/v1/pages/${encodeURIComponent(pageId)}/blocks/${encodeURIComponent(blockId)}`, { parent_block_id: parentBlockId }),
};
